import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { nationsService, categoriesService, ageGroupsService } from "../../services";
import FlagSelectTailwind from './FlagSelectTailwind';
import GenericSelectTailwind from './GenericSelectTailwind';

const ParticipantFormTailwind = forwardRef(({ 
  participant = null, 
  onSave, 
  onCancel, 
  isSaving = false 
}, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    nation: "",
    nationCode: "",
    age: "",
    ageGroup: "",
    gender: "",
    category: [],
    openFormName: "",
    status: "Ausstehend"
  });
  const [nations, setNations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableOpenForms, setAvailableOpenForms] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Expose handleSubmit to parent component
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (validateForm()) {
        // Konvertiere Namen zu IDs für das Backend
        const selectedNation = nations.find(n => n.code === formData.nationCode);
        const nationId = selectedNation ? selectedNation.id : null;
        
        // Konvertiere Kategorienamen zu Kategorie-IDs
        const categoryIds = formData.category.map(categoryName => {
          const category = categories.find(cat => cat.name === categoryName);
          return category ? category.id : null;
        }).filter(id => id !== null);
        
        // Erstelle Backend-kompatible Daten
        const backendData = {
          name: formData.name,
          age: parseInt(formData.age),
          gender: formData.gender,
          status: formData.status,
          nationId: nationId,
          categoryIds: categoryIds,
          openFormName: formData.openFormName
        };
        
        onSave(backendData);
      }
    }
  }));

  // Lade Referenzdaten
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Initialisiere Form-Daten wenn ein Teilnehmer zum Bearbeiten übergeben wird
  useEffect(() => {
    if (participant) {
      setFormData({
        name: participant.name || "",
        nation: participant.nation?.name || participant.nation || "",
        nationCode: participant.nation?.code || participant.nationCode || "",
        age: participant.age?.toString() || "",
        ageGroup: participant.ageGroup?.name || participant.ageGroup || "",
        gender: participant.gender || "",
        category: (participant.categories || participant.category || []).map(cat => cat.name || cat),
        openFormName: participant.openFormName || "",
        status: participant.status || "Ausstehend"
      });
    }
  }, [participant]);

  // Aktualisiere verfügbare Kategorien wenn sich Altersgruppe ändert
  useEffect(() => {
    if (formData.ageGroup && categories.length > 0) {
      // Filtere Kategorien basierend auf der Altersgruppe
      const filteredCategories = categories.filter(cat => 
        cat.ageGroups && cat.ageGroups.includes(formData.ageGroup)
      );
      
      // Trenne normale Kategorien von offenen Formen
      const normalCategories = filteredCategories.filter(cat => cat.type !== "Offene Form");
      const openForms = filteredCategories.filter(cat => cat.type === "Offene Form");
      
      setAvailableCategories(normalCategories);
      setAvailableOpenForms(openForms);
      
      // Entferne Kategorien aus der Auswahl, die nicht mehr verfügbar sind
      const validCategories = formData.category.filter(cat => 
        normalCategories.some(availableCat => availableCat.name === cat) ||
        openForms.some(availableCat => availableCat.name === cat)
      );
      
      if (validCategories.length !== formData.category.length) {
        setFormData(prev => ({
          ...prev,
          category: validCategories
        }));
      }
    } else {
      setAvailableCategories([]);
      setAvailableOpenForms([]);
    }
  }, [formData.ageGroup, categories]);

  const loadReferenceData = async () => {
    try {
      const [nationsResponse, categoriesResponse, ageGroupsResponse] = await Promise.all([
        nationsService.getAllNations(),
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (nationsResponse.success) {
        setNations(nationsResponse.data);
      }
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }
      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Referenzdaten:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Fehler für dieses Feld entfernen
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleAgeChange = async (age) => {
    const ageNum = parseInt(age);
    
    // Altersgruppe automatisch ermitteln
    let autoAgeGroup = "";
    if (ageNum > 0) {
      try {
        const ageGroupResponse = await ageGroupsService.getAgeGroupForAge(ageNum);
        if (ageGroupResponse.success && ageGroupResponse.data) {
          autoAgeGroup = ageGroupResponse.data.name;
        }
      } catch (error) {
        console.error('Fehler bei der Altersgruppen-Bestimmung:', error);
        // Fallback: Lokale Bestimmung
        if (ageNum >= 8 && ageNum <= 13) autoAgeGroup = "Kinder";
        else if (ageNum >= 14 && ageNum <= 19) autoAgeGroup = "Jugend";
        else if (ageNum >= 20 && ageNum <= 39) autoAgeGroup = "Erwachsene";
        else if (ageNum >= 40 && ageNum <= 54) autoAgeGroup = "Senioren";
        else if (ageNum >= 55) autoAgeGroup = "Senioren Plus";
      }
    }

    setFormData(prev => ({
      ...prev,
      age: age,
      ageGroup: autoAgeGroup
    }));

    // Fehler für diese Felder entfernen
    if (errors.age) {
      setErrors(prev => ({
        ...prev,
        age: ""
      }));
    }
    if (errors.ageGroup) {
      setErrors(prev => ({
        ...prev,
        ageGroup: ""
      }));
    }
  };

  const handleCategoryChange = (categoryName, checked) => {
    setFormData(prev => {
      let newCategories = [...prev.category];
      
      if (checked) {
        // Prüfe ob bereits 3 Kategorien ausgewählt sind (inkl. offene Formen)
        if (newCategories.length >= 3) {
          return prev; // Keine Änderung
        }
        newCategories.push(categoryName);
      } else {
        newCategories = newCategories.filter(cat => cat !== categoryName);
      }
      
      return {
        ...prev,
        category: newCategories
      };
    });
  };

  const handleOpenFormChange = (openFormName, checked) => {
    setFormData(prev => {
      let newCategories = [...prev.category];
      
      // Entferne alle offenen Formen zuerst
      newCategories = newCategories.filter(cat => 
        !availableOpenForms.some(openForm => openForm.name === cat)
      );
      
      if (checked) {
        // Prüfe ob bereits 3 Kategorien ausgewählt sind (inkl. offene Formen)
        if (newCategories.length >= 3) {
          return prev; // Keine Änderung
        }
        // Füge nur die ausgewählte offene Form hinzu
        newCategories.push(openFormName);
      }
      
      return {
        ...prev,
        category: newCategories,
        openFormName: checked ? "" : "" // Reset Form-Name wenn offene Form entfernt wird
      };
    });
  };

  const getSelectedOpenForm = () => {
    return formData.category.find(cat => 
      availableOpenForms.some(openForm => openForm.name === cat)
    );
  };

  const isCategoryDisabled = (categoryName) => {
    const isSelected = formData.category.includes(categoryName);
    const isOpenForm = availableOpenForms.some(openForm => openForm.name === categoryName);
    
    // Wenn bereits 3 Kategorien ausgewählt sind und diese nicht ausgewählt ist
    if (formData.category.length >= 3 && !isSelected) {
      return true;
    }
    
    return false;
  };

  const isOpenFormDisabled = (openFormName) => {
    const isSelected = formData.category.includes(openFormName);
    
    // Wenn bereits 3 Kategorien ausgewählt sind und diese offene Form nicht ausgewählt ist
    if (formData.category.length >= 3 && !isSelected) {
      return true;
    }
    
    return false;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name ist erforderlich";
    }

    if (!formData.nationCode) {
      newErrors.nation = "Nation ist erforderlich";
    }

    if (!formData.age || formData.age < 8 || formData.age > 100) {
      newErrors.age = "Gültiges Alter ist erforderlich (8-100)";
    }

    if (!formData.gender) {
      newErrors.gender = "Geschlecht ist erforderlich";
    }

    if (!formData.ageGroup) {
      newErrors.ageGroup = "Altersgruppe ist erforderlich";
    }

    if (formData.category.length === 0) {
      newErrors.category = "Mindestens eine Kategorie muss ausgewählt werden";
    }

    // Prüfe ob eine offene Form ausgewählt ist und Form-Name erforderlich ist
    const selectedOpenForm = getSelectedOpenForm();
    if (selectedOpenForm && !formData.openFormName.trim()) {
      newErrors.openFormName = "Form-Name ist erforderlich bei offenen Formen";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-primary-light"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t("admin.loading", "Daten werden geladen...")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          {t("admin.participants.form.name", "Name")} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          className={`w-full px-3 py-2.5 border rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 ${
            errors.name ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/30' : 'border-slate-200 dark:border-slate-700'
          }`}
          placeholder={t("admin.participants.form.namePlaceholder", "Vollständiger Name")}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
        )}
      </div>

      {/* Nation und Alter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t("admin.participants.form.nation", "Nation")} <span className="text-red-500">*</span>
          </label>
          <FlagSelectTailwind
            value={formData.nationCode}
            onChange={(value) => {
              const selectedNation = nations.find(n => n.code === value);
              setFormData(prev => ({
                ...prev,
                nation: selectedNation ? selectedNation.name : "",
                nationCode: value
              }));
            }}
            options={nations}
            placeholder="Nation auswählen..."
          />
          {errors.nation && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nation}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t("admin.participants.form.age", "Alter")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData.age}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) {
                handleAgeChange(val);
              }
            }}
            className={`w-full px-3 py-2.5 border rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 ${
              errors.age ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/30' : 'border-slate-200 dark:border-slate-700'
            }`}
            placeholder="18"
          />
          {errors.age && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.age}</p>
          )}
        </div>
      </div>

      {/* Geschlecht und Altersgruppe */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t("admin.participants.form.gender", "Geschlecht")} <span className="text-red-500">*</span>
          </label>
          <GenericSelectTailwind
            value={formData.gender}
            onChange={(value) => handleInputChange("gender", value)}
            options={[
              { value: "männlich", label: t("admin.participants.gender.male", "Männlich"), icon: <i className="fas fa-mars text-blue-600"></i> },
              { value: "weiblich", label: t("admin.participants.gender.female", "Weiblich"), icon: <i className="fas fa-venus text-pink-600"></i> }
            ]}
            placeholder={t("admin.participants.form.selectGender", "Geschlecht auswählen")}
            leftIcon={<i className="fas fa-venus-mars text-slate-400 dark:text-slate-500"></i>}
          />
          {errors.gender && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.gender}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t("admin.participants.form.ageGroup", "Altersgruppe")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.ageGroup}
            readOnly
            className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 cursor-not-allowed"
            placeholder={t("admin.participants.form.ageGroupAuto", "Wird automatisch ermittelt")}
          />
          {formData.ageGroup && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Automatisch ermittelt basierend auf dem Alter
            </p>
          )}
          {errors.ageGroup && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.ageGroup}</p>
          )}
        </div>
      </div>

      {/* Normale Kategorien */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          {t("admin.participants.form.categories", "Kategorien")} <span className="text-red-500">*</span>
          {formData.ageGroup && (
            <span className="text-slate-500 dark:text-slate-400 text-sm font-normal ml-2">
              (Max. 3 Kategorien, verfügbar für {formData.ageGroup})
            </span>
          )}
        </label>
        {formData.ageGroup ? (
          <div className="flex flex-wrap gap-2">
            {availableCategories.map((category) => (
              <label 
                key={category.id} 
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-colors cursor-pointer ${
                  isCategoryDisabled(category.name) 
                    ? 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed' 
                    : formData.category.includes(category.name)
                    ? 'bg-primary dark:bg-primary-light text-white border-primary dark:border-primary-light'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-900 dark:text-slate-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.category.includes(category.name)}
                  onChange={(e) => handleCategoryChange(category.name, e.target.checked)}
                  disabled={isCategoryDisabled(category.name)}
                  className="w-4 h-4 text-primary dark:text-primary-light border-slate-300 dark:border-slate-600 rounded focus:ring-primary/20 dark:focus:ring-primary-light/20"
                />
                <span className="text-sm font-medium">{category.name}</span>
                {isCategoryDisabled(category.name) && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">Max. 3</span>
                )}
              </label>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-center text-slate-500 dark:text-slate-400">
            Bitte geben Sie zuerst das Alter ein, um verfügbare Kategorien zu sehen.
          </div>
        )}
        {errors.category && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.category}</p>
        )}
      </div>

      {/* Offene Formen - Separater Bereich */}
      {formData.ageGroup && availableOpenForms.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            Offene Formen
            <span className="text-slate-500 dark:text-slate-400 text-sm font-normal ml-2">
              (Optional, nur eine Form möglich)
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            {availableOpenForms.map((openForm) => (
              <label 
                key={openForm.id} 
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-colors cursor-pointer ${
                  isOpenFormDisabled(openForm.name) 
                    ? 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed' 
                    : formData.category.includes(openForm.name)
                    ? 'bg-primary dark:bg-primary-light text-white border-primary dark:border-primary-light'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-900 dark:text-slate-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.category.includes(openForm.name)}
                  onChange={(e) => handleOpenFormChange(openForm.name, e.target.checked)}
                  disabled={isOpenFormDisabled(openForm.name)}
                  className="w-4 h-4 text-primary dark:text-primary-light border-slate-300 dark:border-slate-600 rounded focus:ring-primary/20 dark:focus:ring-primary-light/20"
                />
                <span className="text-sm font-medium">{openForm.name}</span>
                {isOpenFormDisabled(openForm.name) && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">Max. 3</span>
                )}
              </label>
            ))}
          </div>
          
          {/* Form-Name Eingabe für offene Formen */}
          {getSelectedOpenForm() && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                Form-Name für {getSelectedOpenForm()} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.openFormName}
                onChange={(e) => handleInputChange("openFormName", e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 ${
                  errors.openFormName ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/30' : 'border-slate-200 dark:border-slate-700'
                }`}
                placeholder="Name der eigenen Form eingeben"
              />
              {errors.openFormName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.openFormName}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          {t("admin.participants.form.status", "Status")}
        </label>
        <GenericSelectTailwind
          value={formData.status}
          onChange={(value) => handleInputChange("status", value)}
          options={[
            { value: "Ausstehend", label: t("admin.participants.filters.pending", "Ausstehend"), icon: <i className="fas fa-clock text-yellow-600"></i> },
            { value: "Bestätigt", label: t("admin.participants.filters.confirmed", "Bestätigt"), icon: <i className="fas fa-check-circle text-green-600"></i> }
          ]}
          placeholder={t("admin.participants.form.selectStatus", "Status auswählen")}
          leftIcon={<i className="fas fa-info-circle text-slate-400 dark:text-slate-500"></i>}
        />
      </div>

      {/* Vorschau */}
      {formData.name && formData.nationCode && (
        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">Vorschau:</h4>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary dark:bg-primary-light text-white rounded-full flex items-center justify-center text-sm font-medium">
              {formData.name[0]}{formData.name.split(' ')[1]?.[0] || ''}
            </div>
            <div>
              <div className="font-medium text-slate-900 dark:text-slate-100">{formData.name}</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {formData.age && `${formData.age} Jahre`}
                {formData.ageGroup && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded">
                    {formData.ageGroup}
                  </span>
                )}
              </div>
            </div>
          </div>
          {formData.category.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {formData.category.map((cat, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded"
                >
                  {cat}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ParticipantFormTailwind;